import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, Users, Check, AlertTriangle, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { teamApi } from '../lib/teamApi';
import { useT } from '../lib/i18n';

const STR = {
  fr: {
    no_token:           'Aucun jeton fourni dans le lien.',
    not_found:          'Invitation introuvable.',
    welcome_toast:      (name) => `Bienvenue dans ${name || "l'équipe"} !`,
    accept_error:       "Erreur lors de l'acceptation",
    invitation_to:      'Invitation à rejoindre',
    loading:            'Chargement...',
    default_team:       'une équipe',
    verifying:          "Vérification de l'invitation...",
    invalid_invitation: 'Invitation invalide',
    back_home:          "Retour à l'accueil",
    body_someone:       "Quelqu'un",
    body_invites_pre:   ' t\'invite à rejoindre ',
    body_invites_mid:   ' sur SoumissionPro. En acceptant\u00a0:',
    benefit1:           'Tu accéderas aux soumissions, clients et fournisseurs de la compagnie',
    benefit2:           "Tu pourras créer et gérer des soumissions au nom de l'entreprise",
    benefit3:           "L'abonnement est payé par le propriétaire — aucun frais pour toi",
    invitation_sent:    'Invitation envoyée à',
    accept_signedin:    "J'accepte et je rejoins",
    accept_signedout:   'Créer mon compte pour accepter',
    accept_disclaimer:  "En acceptant, tu confirmes connaître la personne qui t'a invité(e).",
    welcome_team:       "Bienvenue dans l'équipe ! 🎉",
    redirecting:        'Redirection en cours...',
    footer_secure:      'Invitation sécurisée · Lien valide 14 jours',
  },
  en: {
    no_token:           'No token provided in the link.',
    not_found:          'Invitation not found.',
    welcome_toast:      (name) => `Welcome to ${name || 'the team'}!`,
    accept_error:       'Error while accepting',
    invitation_to:      'Invitation to join',
    loading:            'Loading...',
    default_team:       'a team',
    verifying:          'Verifying the invitation...',
    invalid_invitation: 'Invalid invitation',
    back_home:          'Back to home',
    body_someone:       'Someone',
    body_invites_pre:   ' invites you to join ',
    body_invites_mid:   ' on SoumissionPro. By accepting\u00a0:',
    benefit1:           "You'll access the company's quotes, clients and suppliers",
    benefit2:           "You'll be able to create and manage quotes on behalf of the company",
    benefit3:           'The subscription is paid by the owner — no charge for you',
    invitation_sent:    'Invitation sent to',
    accept_signedin:    'I accept and join',
    accept_signedout:   'Create my account to accept',
    accept_disclaimer:  'By accepting, you confirm that you know the person who invited you.',
    welcome_team:       'Welcome to the team! 🎉',
    redirecting:        'Redirecting...',
    footer_secure:      'Secure invitation · Link valid for 14 days',
  },
};

export default function TeamJoin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const { user } = useAuth();
  const { lang } = useT();
  const T = STR[lang === 'en' ? 'en' : 'fr'];
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(T.no_token);
      setLoading(false);
      return;
    }
    teamApi
      .lookupInvitation(token)
      .then((data) => {
        setInvite(data);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message || T.not_found);
        setLoading(false);
      });
  }, [token, T.no_token, T.not_found]);

  const handleAccept = async () => {
    if (!user) {
      try {
        localStorage.setItem('pendingTeamToken', token);
      } catch (e) {
        // Storage failures are non-blocking — the token also lives in the URL.
      }
      const emailHint = invite?.member_email ? `&email=${encodeURIComponent(invite.member_email)}` : '';
      navigate(`/auth?next=/team/join%3Ftoken%3D${encodeURIComponent(token)}${emailHint}`);
      return;
    }
    setAccepting(true);
    try {
      const res = await teamApi.accept(token);
      setAccepted(true);
      toast.success(T.welcome_toast(res.company_name));
      // 🧹 Critical: clear all local IndexedDB + cached team metadata before
      // reloading so the new member doesn't see stale data from their previous
      // (solo) state.
      try {
        const { clearAllData } = await import('../lib/idb');
        await clearAllData();
        Object.keys(localStorage).forEach((k) => {
          if (k.startsWith('soumissionpro:effective_owner:') ||
              k.startsWith('soumissionpro:team_role:') ||
              k.startsWith('soumissionpro:owner_info:')) {
            localStorage.removeItem(k);
          }
        });
      } catch (e) {
        console.warn('[team-join] cache wipe failed', e);
      }
      setTimeout(() => {
        window.location.href = '/';
      }, 1200);
    } catch (e) {
      toast.error(e.message || T.accept_error);
      setAccepting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10" style={{ backgroundColor: '#FAF7F2' }}>
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <div className="text-xs font-bold tracking-widest" style={{ color: '#7A1F2B' }}>
            SOUMISSIONPRO
          </div>
        </div>

        <div
          className="rounded-3xl overflow-hidden bg-white"
          style={{ boxShadow: '0 8px 32px rgba(122,31,43,0.12)' }}
          data-testid="team-join-card"
        >
          {/* Top gradient */}
          <div
            className="px-8 pt-10 pb-8 text-white text-center"
            style={{ background: 'linear-gradient(135deg,#7A1F2B 0%,#5A1620 100%)' }}
          >
            <div
              className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: '#C9A961' }}
            >
              <Users className="w-8 h-8" style={{ color: '#5A1620' }} />
            </div>
            <div className="text-xs uppercase tracking-widest mb-2" style={{ color: '#C9A961' }}>
              {T.invitation_to}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold leading-tight" style={{ letterSpacing: '-0.5px' }}>
              {loading ? T.loading : (invite?.company_name || T.default_team)}
            </h1>
          </div>

          <div className="px-8 py-8">
            {loading && (
              <div className="flex flex-col items-center py-8">
                <Loader2 className="w-6 h-6 animate-spin mb-2" style={{ color: '#7A1F2B' }} />
                <div className="text-sm" style={{ color: '#6B7280' }}>{T.verifying}</div>
              </div>
            )}

            {!loading && error && (
              <div className="text-center" data-testid="team-join-error">
                <AlertTriangle className="w-10 h-10 mx-auto mb-3" style={{ color: '#B45309' }} />
                <p className="font-semibold mb-1" style={{ color: '#5A1620' }}>{T.invalid_invitation}</p>
                <p className="text-sm" style={{ color: '#6B7280' }}>{error}</p>
                <button
                  onClick={() => navigate('/')}
                  className="mt-6 px-5 py-2.5 rounded-xl font-semibold text-sm"
                  style={{ color: '#7A1F2B', border: '2px solid #7A1F2B' }}
                  data-testid="team-join-home-btn"
                >
                  {T.back_home}
                </button>
              </div>
            )}

            {!loading && !error && invite && !accepted && (
              <>
                <p className="text-sm leading-relaxed mb-5" style={{ color: '#4A5568' }}>
                  <strong style={{ color: '#5A1620' }}>{invite.invited_by_name || T.body_someone}</strong>
                  {T.body_invites_pre}
                  <strong style={{ color: '#5A1620' }}>{invite.company_name}</strong>
                  {T.body_invites_mid}
                </p>
                <ul className="space-y-2.5 mb-7">
                  <li className="flex items-start gap-2.5 text-sm" style={{ color: '#374151' }}>
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#16A34A' }} />
                    <span>{T.benefit1}</span>
                  </li>
                  <li className="flex items-start gap-2.5 text-sm" style={{ color: '#374151' }}>
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#16A34A' }} />
                    <span>{T.benefit2}</span>
                  </li>
                  <li className="flex items-start gap-2.5 text-sm" style={{ color: '#374151' }}>
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#16A34A' }} />
                    <span>{T.benefit3}</span>
                  </li>
                </ul>

                <div
                  className="rounded-xl p-3 mb-5"
                  style={{ backgroundColor: '#FAF7F2', border: '1px solid #E5DCC9' }}
                >
                  <div className="text-xs uppercase tracking-widest mb-1" style={{ color: '#7A1F2B' }}>
                    {T.invitation_sent}
                  </div>
                  <div className="text-sm font-semibold" style={{ color: '#5A1620' }}>
                    {invite.member_email}
                  </div>
                </div>

                <button
                  onClick={handleAccept}
                  disabled={accepting}
                  className="w-full py-3.5 rounded-xl font-semibold text-white inline-flex items-center justify-center gap-2 transition disabled:opacity-50"
                  style={{
                    backgroundColor: '#7A1F2B',
                    boxShadow: '0 4px 14px rgba(122,31,43,0.25)',
                  }}
                  data-testid="team-join-accept-btn"
                >
                  {accepting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      {user ? T.accept_signedin : T.accept_signedout}
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                <p className="text-center text-xs mt-4" style={{ color: '#9CA3AF' }}>
                  {T.accept_disclaimer}
                </p>
              </>
            )}

            {accepted && (
              <div className="text-center py-6" data-testid="team-join-success">
                <div
                  className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center"
                  style={{ backgroundColor: '#16A34A22' }}
                >
                  <Check className="w-7 h-7" style={{ color: '#16A34A' }} />
                </div>
                <p className="font-bold text-lg mb-1" style={{ color: '#5A1620' }}>
                  {T.welcome_team}
                </p>
                <p className="text-sm" style={{ color: '#6B7280' }}>
                  {T.redirecting}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="text-center mt-5 text-xs" style={{ color: '#9CA3AF' }}>
          <ShieldCheck className="w-3 h-3 inline mr-1" />
          {T.footer_secure}
        </div>
      </div>
    </div>
  );
}
